import { createFeatureSelector, createSelector } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';

import * as Objects from '@core/objects';
import * as Search from '@app/models/search';

import { VersionFeedback } from '@core/models/version-feedback';


export class FiltersHttpStatus {
    static readonly ERROR: number = 500;
    static readonly INITIAL: number = 100;
    static readonly LOADING: number = 200;
    static readonly DONE: number = 300;
    static readonly TIMEOUT: number = 400;
}

export interface ApprovalsAvailableFiltersStatus {
    labels: {
        status: number;
    },
    users: {
        status: number;
    }
}

export interface ApprovalsResultsStatus {
    uncompleted: number;
    completed: number;
}

export interface ApprovalsResultsItems {
    completed: Array<Objects.Version>;
    uncompleted: Array<Objects.Version>;
}

interface ApprovalsAvailableFilters {
    labels: Search.SearchAvailableFilters;
    users: Search.SearchAvailableFilters;
    initialized_status: ApprovalsAvailableFiltersStatus;
}

interface ApprovalsSearch {
    term: string;
    limit: number;
    filters: Array<Search.SearchAppliedFilter>;
}

interface ApprovalsResults {
    next_page_completed: number;
    next_page_uncompleted: number;
    status: ApprovalsResultsStatus;
    uncompleted_items: Array<Objects.Version>;
    completed_items: Array<Objects.Version>;
    selected_status: string;
}

export interface ApprovalsResultsFeedbacks {
    uncompleted: Array<VersionFeedback>;
    completed: Array<VersionFeedback>;
    feedbackAccess: boolean;
    globalAccess: boolean | string; // teh global aceess control true/false/"own"
    status: ApprovalsResultsStatus;
}

// in snake style for help to find
interface ApprovalsErrors {
    load_results: HttpErrorResponse,
}


interface ApprovalsStart {
    contexts: {
        session: {
            value: any;
            loaded: boolean;
        };
        url: {
            value: any;
            loaded: boolean;
        };
    },
    initialized: boolean;
}

export interface Approvals {
    starting: ApprovalsStart;
    available_filters:  ApprovalsAvailableFilters;
    search: ApprovalsSearch;
    search_query_params: Search.SearchQueryParams;
    results: ApprovalsResults;
    feedbacks: ApprovalsResultsFeedbacks;
    events: Array<{name: string, id: number | string}>;
    errors: ApprovalsErrors;
}

export const initialState: Approvals = {
    starting: {
        initialized: false,
        contexts: {
            session: {
                value: null,
                loaded: false
            },
            url: {
                value: null,
                loaded: false
            }
        }
    },
    available_filters : {
        labels: {
            key: "label",
            items: []
        },
        users: {
            key: "feedback_user",
            items: []
        },
        initialized_status: {
            labels: {
                status: FiltersHttpStatus.INITIAL,
            },
            users: {
                status: FiltersHttpStatus.INITIAL,
            }
        }
    },
    search: {
        term: "",
        limit: 50,
        filters: [],
    },
    search_query_params: null,
    results: {
        next_page_completed: 1,
        next_page_uncompleted: 1,
        status: {
            uncompleted: Search.SearchHttpStatus.INITIAL,
            completed: Search.SearchHttpStatus.INITIAL
        },
        uncompleted_items: null,
        completed_items: null,
        selected_status: ''
    },
    feedbacks: {
        uncompleted: [],
        completed: [],
        feedbackAccess: null,
        globalAccess: null,
        status: {
            uncompleted: Search.SearchHttpStatus.INITIAL,
            completed: Search.SearchHttpStatus.INITIAL
        },
    },
    events: [],
    errors: {
        load_results: null,
    }
};

const approvalsFeatureSelector = createFeatureSelector('approvals');

// Selectors
export const approvalsSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state
);

export const startingSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.starting
);

export const availableFiltersSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.available_filters
);

export const availableFiltersInitializedStatusSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.available_filters.initialized_status
);


export const availableFilterLabelsSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.available_filters.labels
);

export const availableFilterUsersSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.available_filters.users
);

export const searchSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.search
);

export const searchFiltersSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.search.filters
);

export const searchTermSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.search.term
);

export const queryParamsSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.search_query_params
);

export const resultSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.results
);

export const resultUncompletedItemsSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.results.uncompleted_items
);

export const resultCompletedItemsSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.results.completed_items
);

export const resultStatusSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.results.status
);

export const resultItemsSelector =  createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => {
        if(state.results.selected_status != '' && state.results.selected_status == 'completed')
            return state.results.completed_items;
        if(state.results.selected_status != '' && state.results.selected_status == 'uncompleted')
            return state.results.uncompleted_items;
        return null;
    }
);

export const resultSelectStatusSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.results.selected_status
);

export const resultFeedbacksSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.feedbacks.uncompleted
);

export const resultCompletedFeedbacksSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.feedbacks.completed
);

export const resultFeedbacksStatusSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.feedbacks.status
);

export const eventsSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.events
);

// global selectors
export const approvalsResultsFeedbacksSelector = createSelector(
    approvalsFeatureSelector,
    (state: Approvals) => state.feedbacks
);

export const errorSelector = (key: string) => 
    createSelector(
        approvalsFeatureSelector,
        (state: Approvals) => state.errors.hasOwnProperty(key) ? state.errors[key] : null
    );